import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, UserPlus, ArrowLeft,
  Star, Trophy, Flame, TrendingUp, Clock, Target,
  Calculator, BookOpen, FlaskConical, Award, FileDown, Loader2
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const ChildProgress = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const sidebarItems = [
    { path: '/parent', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/parent/register-child', label: 'Register Child', icon: UserPlus },
  ];

  const downloadProgressReport = async () => {
    setDownloadingPdf(true);
    try {
      const response = await api.get(`/progress-report/${childId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `progress_report_${data?.child?.first_name || 'student'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Progress report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download progress report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await api.get(`/parent/child/${childId}/progress`);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching child progress:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [api, childId]);

  const subjectInfo = {
    maths: { icon: Calculator, color: 'amber', name: 'Mathematics' },
    english: { icon: BookOpen, color: 'pink', name: 'English' },
    science: { icon: FlaskConical, color: 'emerald', name: 'Science' },
    esol: { icon: BookOpen, color: 'blue', name: 'ESOL' },
    '11plus': { icon: Target, color: 'purple', name: '11+ Preparation' },
  };

  const keyStageLabels = {
    ks1: 'Key Stage 1 (Years 1-2)',
    ks2: 'Key Stage 2 (Years 3-6)',
    ks3: 'Key Stage 3 (Years 7-9)',
    ks4: 'Key Stage 4 (Years 10-11)',
    esol: 'ESOL',
    '11plus': '11+ Preparation'
  };

  if (loading) {
    return (
      <ProLayout title="Child Progress" sidebarItems={sidebarItems}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
        </div>
      </ProLayout>
    );
  }

  if (!data) {
    return (
      <ProLayout title="Child Progress" sidebarItems={sidebarItems}>
        <div className="text-center py-12">
          <p className="text-slate-500">Could not load child data</p>
          <Link to="/parent">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </ProLayout>
    );
  }

  const { child, progress_by_subject, achievements, total_lessons } = data;

  return (
    <ProLayout title={`${child.first_name}'s Progress`} sidebarItems={sidebarItems}>
      <div data-testid="child-progress">
        {/* Back Button */}
        <Link to="/parent" className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* Child Header */}
        <div className="pro-card bg-gradient-to-r from-emerald-500 to-teal-600 text-white mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <span className="font-bold text-white text-2xl">
                  {child.first_name?.charAt(0)}
                </span>
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">
                  {child.first_name} {child.last_name}
                </h1>
                <p className="text-emerald-100">
                  Year {child.year_group} • {keyStageLabels[child.key_stage]}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="flex items-center gap-2 mb-1">
                    <Flame className="w-5 h-5 text-orange-300" />
                    <span className="text-2xl font-bold">{child.streak_days || 0}</span>
                  </div>
                  <p className="text-xs text-emerald-100">Day Streak</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="w-5 h-5 text-yellow-300" />
                    <span className="text-2xl font-bold">{child.total_points || 0}</span>
                  </div>
                  <p className="text-xs text-emerald-100">Total Points</p>
                </div>
              </div>
              
              <Button
                onClick={downloadProgressReport}
                disabled={downloadingPdf}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                variant="outline"
                data-testid="download-report-btn"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Download Report
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="pro-card text-center">
            <TrendingUp className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{total_lessons}</p>
            <p className="text-sm text-slate-500">Lessons Completed</p>
          </div>
          <div className="pro-card text-center">
            <Trophy className="w-8 h-8 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{achievements?.length || 0}</p>
            <p className="text-sm text-slate-500">Achievements</p>
          </div>
          <div className="pro-card text-center">
            <Star className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{child.total_stars || 0}</p>
            <p className="text-sm text-slate-500">Stars Earned</p>
          </div>
          <div className="pro-card text-center">
            <Target className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">Level {child.level || 1}</p>
            <p className="text-sm text-slate-500">Current Level</p>
          </div>
        </div>

        {/* Progress by Subject */}
        <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Progress by Subject</h2>
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {Object.entries(progress_by_subject).length === 0 ? (
            <div className="col-span-3 pro-card text-center py-8">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No lessons completed yet</p>
            </div>
          ) : (
            Object.entries(progress_by_subject).map(([subject, stats]) => {
              const info = subjectInfo[subject] || { icon: BookOpen, color: 'gray', name: subject };
              return (
                <div key={subject} className="pro-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 bg-${info.color}-100 rounded-lg flex items-center justify-center`}>
                      <info.icon className={`w-6 h-6 text-${info.color}-600`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 capitalize">{info.name}</h3>
                      <p className="text-sm text-slate-500">{stats.completed} lessons</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Points Earned</span>
                      <span className="font-semibold text-slate-800">{stats.total_score}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Time Spent</span>
                      <span className="font-semibold text-slate-800">{stats.time_minutes} mins</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Achievements */}
        <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Recent Achievements</h2>
        <div className="pro-card">
          {!achievements || achievements.length === 0 ? (
            <div className="text-center py-8">
              <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No achievements unlocked yet</p>
              <p className="text-sm text-slate-400 mt-1">Encourage your child to complete lessons to earn badges!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {achievements.slice(0, 8).map((ach) => (
                <div key={ach.achievement_id} className="text-center p-4 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Trophy className="w-6 h-6 text-amber-600" />
                  </div>
                  <p className="font-semibold text-slate-700 text-sm capitalize">
                    {ach.achievement_id.replace(/-/g, ' ')}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(ach.unlocked_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ProLayout>
  );
};

export default ChildProgress;
